import { useQuery } from "@tanstack/react-query";
import { BellRing, Loader2 } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { NevoraDataProvider } from "../datos/dataProvider";
import {
  claveAplicacion,
  estadoPush,
  nombreDispositivo,
  type Navegador,
} from "../dominio/push";
import { formatoFechaHora } from "../negocios/formato";

/**
 * «Avisos en este móvil» (33, Robert 16/10/2026): cada uno activa las notificaciones push en su
 * móvil. En iPhone, solo con la app instalada en la pantalla de inicio; el permiso se pide al tocar
 * el botón. Sin la 33 en la base, no sale.
 */
export const AvisosMovil = () => {
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { data: disponible } = useQuery({
    queryKey: ["nevora", "pushDisponible"],
    queryFn: () => dataProvider.pushDisponible(),
    staleTime: Infinity,
    retry: false,
  });
  return disponible ? <AvisosMovilContenido /> : null;
};

const navegadorActual = (): Navegador => ({
  serviceWorker: "serviceWorker" in navigator,
  pushManager: "PushManager" in window,
  notification: "Notification" in window,
  userAgent: navigator.userAgent,
  maxTouchPoints: navigator.maxTouchPoints ?? 0,
  instalada:
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true,
});

const AvisosMovilContenido = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [estado] = useState(() => estadoPush(navegadorActual()));
  const [suscripcion, setSuscripcion] = useState<PushSubscription | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const permiso =
    "Notification" in window ? Notification.permission : ("default" as const);
  const { data: moviles, refetch } = useQuery({
    queryKey: ["nevora", "misMovilesPush"],
    queryFn: () => dataProvider.misMovilesPush(),
  });

  const guardar = async (sub: PushSubscription) => {
    const j = sub.toJSON();
    await dataProvider.guardarMovilPush({
      endpoint: j.endpoint!,
      p256dh: j.keys!.p256dh,
      auth: j.keys!.auth,
      dispositivo: nombreDispositivo(
        navigator.userAgent,
        navigator.maxTouchPoints ?? 0,
      ),
    });
  };

  // Al abrir: si este móvil ya está activado, se vuelve a guardar (por si la base lo perdió).
  useEffect(() => {
    if (estado !== "si") return;
    let vivo = true;
    void (async () => {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!vivo) return;
      setSuscripcion(sub);
      if (sub) {
        await guardar(sub).catch(() => undefined);
        void refetch();
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  const activar = async () => {
    setTrabajando(true);
    try {
      // Primero el permiso, directamente desde el toque (iPhone lo exige).
      const p = await Notification.requestPermission();
      if (p !== "granted") {
        notify("nevora.avisos_movil.permiso_denegado", { type: "warning" });
        return;
      }
      const clave = await dataProvider.clavePublicaPush();
      const reg = await navigator.serviceWorker.ready;
      const anterior = await reg.pushManager.getSubscription();
      if (anterior) await anterior.unsubscribe().catch(() => undefined);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: claveAplicacion(clave) as BufferSource,
      });
      await guardar(sub);
      setSuscripcion(sub);
      await refetch();
      notify("nevora.avisos_movil.activadas", { type: "success" });
    } catch {
      notify("nevora.avisos_movil.error", { type: "error" });
    } finally {
      setTrabajando(false);
    }
  };

  const desactivar = async () => {
    if (!suscripcion) return;
    setTrabajando(true);
    try {
      await dataProvider.quitarMovilPush({ endpoint: suscripcion.endpoint });
      await suscripcion.unsubscribe().catch(() => undefined);
      setSuscripcion(null);
      await refetch();
      notify("nevora.avisos_movil.desactivadas", { type: "success" });
    } catch {
      notify("nevora.avisos_movil.error", { type: "error" });
    } finally {
      setTrabajando(false);
    }
  };

  const prueba = async () => {
    setTrabajando(true);
    try {
      const r = await dataProvider.pruebaPush();
      notify("nevora.avisos_movil.prueba_enviada", {
        type: "info",
        messageArgs: { smart_count: r.enviadas },
      });
    } catch {
      notify("nevora.avisos_movil.error", { type: "error" });
    } finally {
      setTrabajando(false);
    }
  };

  const quitarOtro = async (id: string | number) => {
    try {
      await dataProvider.quitarMovilPush({ id });
      await refetch();
    } catch {
      notify("nevora.avisos_movil.error", { type: "error" });
    }
  };

  return (
    <section
      className="rounded-lg border bg-card px-5 py-4 space-y-3"
      data-testid="avisos-movil"
    >
      <div className="flex items-center gap-2">
        <BellRing className="size-5" aria-hidden />
        <h2 className="titulo-seccion">
          {translate("nevora.avisos_movil.titulo")}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">
        {translate("nevora.avisos_movil.ayuda")}
      </p>

      {estado === "instalar_primero" ? (
        <ol
          className="list-decimal pl-5 text-sm space-y-1"
          data-testid="avisos-movil-instalar"
        >
          <li>{translate("nevora.avisos_movil.iphone_1")}</li>
          <li>{translate("nevora.avisos_movil.iphone_2")}</li>
          <li>{translate("nevora.avisos_movil.iphone_3")}</li>
        </ol>
      ) : estado === "no" ? (
        <p className="text-sm">{translate("nevora.avisos_movil.no_puede")}</p>
      ) : permiso === "denied" ? (
        <p className="text-sm">{translate("nevora.avisos_movil.bloqueadas")}</p>
      ) : suscripcion ? (
        <div className="space-y-2">
          <p className="text-sm font-medium" data-testid="avisos-movil-activas">
            {translate("nevora.avisos_movil.este_activo")}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={prueba} disabled={trabajando}>
              {translate("nevora.avisos_movil.prueba")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={desactivar}
              disabled={trabajando}
            >
              {translate("nevora.avisos_movil.desactivar")}
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={activar} disabled={trabajando}>
          {trabajando ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
          {translate("nevora.avisos_movil.activar")}
        </Button>
      )}

      {moviles?.length ? (
        <div className="space-y-1 pt-1">
          <p className="text-xs font-medium text-muted-foreground">
            {translate("nevora.avisos_movil.mis_moviles")}
          </p>
          <ul className="divide-y text-sm">
            {moviles.map((m) => {
              const este = m.endpoint === suscripcion?.endpoint;
              return (
                <li
                  key={String(m.id)}
                  className="flex flex-wrap items-center gap-2 py-1.5"
                >
                  <span className="font-medium">
                    {m.dispositivo || translate("nevora.avisos_movil.aparato")}
                  </span>
                  {este ? (
                    <Badge variant="outline">
                      {translate("nevora.avisos_movil.este")}
                    </Badge>
                  ) : null}
                  <span className="text-xs text-muted-foreground flex-1">
                    {m.ultimo_envio_at
                      ? translate("nevora.avisos_movil.ultimo_envio", {
                          fecha: formatoFechaHora(m.ultimo_envio_at),
                        })
                      : translate("nevora.avisos_movil.desde", {
                          fecha: formatoFechaHora(m.created_at),
                        })}
                  </span>
                  {!este ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => quitarOtro(m.id)}
                    >
                      {translate("nevora.avisos_movil.quitar")}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
